import React, { useState, useContext, useEffect } from "react";
import { Modal, Button, Row, Col, Form, Spinner as RBSpinner, Tab, Tabs } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faSearch, faLink, faImages, faArrowLeft, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FileUploadForm } from "../components/CreateVisForms";
import SearchComponent from "../components/SearchComponent";
import { APIWebVisualization } from "../helper/APIFunctions";
import CarouselVisCard from "./CarouselVisCard";
import CarouselPresentationCard from "./CarouselPresentationCard";
import CarouselExplainerCard from "./CarouselExplainerCard";
import { VisualCache } from "../helper/Context";
import { WebUploadForm } from "./CreateVisForms";

const VisualPickerModal = ({ show, onHide, onSelect, userInfo, defaultView, onBackToOptions, filterVisualIdsList = [], owner = "self", initialSearchQuery = '', lightTheme = false, allowAllContentTypes = false, additionalVisuals = [] }) => {
  const [modalView, setModalView] = useState(defaultView || "options");
  const [selectedVisual, setSelectedVisual] = useState(null);
  const [searchResults, setSearchResults] = useState({ res: [], ready: true });
  const [searchTerm, setSearchTerm] = useState(initialSearchQuery);
  const [selectedVisualTab, setSelectedVisualTab] = useState("visuals");
  const [isCreatingVisual, setIsCreatingVisual] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { visCache } = useContext(VisualCache);
  const [visList, setVisList] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (defaultView === 'search' && initialSearchQuery) {
      setModalView('search');
      setSearchTerm(initialSearchQuery);
      handleSearch(initialSearchQuery);
    }
  }, [defaultView, initialSearchQuery]);

  useEffect(() => {
    const cacheVisuals = Object.values(visCache || {})
      .filter(v => v && v.id);
    
    // Merge cache visuals with additional visuals (e.g., presentations passed from parent)
    const allVisuals = [...cacheVisuals, ...additionalVisuals];
    
    // Remove duplicates based on ID
    const uniqueVisuals = allVisuals.reduce((acc, visual) => {
      if (!acc.find(v => v.id === visual.id)) {
        acc.push(visual);
      }
      return acc;
    }, []);
    
    // If allowAllContentTypes is false, filter out presentations and scrollytelling (original behavior)
    const typeFiltered = allowAllContentTypes 
      ? uniqueVisuals 
      : uniqueVisuals.filter(v => v.type !== 'presentation' && v.type !== 'scrollytelling');
    
    setVisList(typeFiltered
      .filter(v => owner === "team" ? v.isOrgVis : !v.isOrgVis)
      .sort((a, b) => parseFloat(b.created_timestamp || 0) - parseFloat(a.created_timestamp || 0))
      .sort((a, b) => (b.favorite || 0) - (a.favorite || 0)));
    setInitialized(true);
  }, [visCache, allowAllContentTypes, owner, additionalVisuals]);

  const handleBackToOptions = () => {
    if (onBackToOptions) {
      onBackToOptions();
    } else {
      setModalView("options");
    }
  };
  const updateVisFilterTerm = (term) => {
    const visualCards = document.querySelectorAll(".picker-visual-card");
    let showCount = 0;
    const emptySearchResults = document.getElementById("empty-search-results");
    const visableType = selectedVisualTab === "visuals" ? "visuals" : "assets";
    if (term && term.length > 0) {
      visualCards.forEach(card => {
        const showCard = (card.dataset?.visualTitle?.toLowerCase()?.includes(term.toLowerCase()) || card.dataset?.visualDescription?.toLowerCase()?.includes(term.toLowerCase())) && card.dataset?.type === visableType;
        card.style.display = showCard ? "block" : "none";
        showCount += showCard;
      });
      if (emptySearchResults) {
        emptySearchResults.style.display = showCount > 0 ? "none" : "block";
      }
    }
    else {
      visualCards.forEach(card => card.style.display = card.dataset?.type === visableType ? "block" : "none");
      if (emptySearchResults) {
        emptySearchResults.style.display = "none";
      }
    }
  };

  useEffect(() => {
    if (modalView === "my-visuals") {
      const searchBar = document.getElementById("my-visuals-search-bar");
      updateVisFilterTerm(searchBar?.value || "");
    }
  }, [modalView, selectedVisualTab, visList, visCache]);

  const handleAddVisual = (visual) => {
    // This function is now just a success handler for when a visual is created/selected
    setSelectedVisual(visual);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
      setModalView("options");
      setSelectedVisual(null);
      onSelect(visual);
      onHide();
    }, 1000);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setSearchResults({ res: [], ready: false });
    // APISearchVisuals(
    //   term,
    //   (res) => {
    //     setSearchResults({ res: res.sources || [], ready: true });
    //   },
    //   () => {
    //     setSearchResults({ res: [], ready: true });
    //   }
    // );
  };

  const renderModalContent = () => {
    if (showSuccessMessage) {
      return (
        <div className="text-center p-4">
          <FontAwesomeIcon icon={faCheck} size="3x" className="text-success mb-3" />
          <h4>Visual Selected!</h4>
          <p>Closing automatically...</p>
        </div>
      );
    }
    //For speed and responsiveness, we're not using state/render logic to filter the visuals, instead we're using the DOM/vanilla JS to filter them.
    switch (modalView) {
      case "options":
        return (
          <Row className="g-3">
            <Col xs={12}>
              <Button variant="outline-primary" className="w-100 p-3" onClick={() => setModalView("upload")}> <FontAwesomeIcon icon={faUpload} className="me-2" size="lg" /> <div>Upload Images or Models</div> </Button>
            </Col>
            <Col xs={12}>
              <Button variant="outline-primary" className="w-100 p-3" onClick={() => setModalView("search")}> <FontAwesomeIcon icon={faSearch} className="me-2" size="lg" /> <div>Search</div> </Button>
            </Col>
            <Col xs={12}>
              <Button variant="outline-primary" className="w-100 p-3" onClick={() => setModalView("web")}> <FontAwesomeIcon icon={faLink} className="me-2" size="lg" /> <div>Use Web Resource</div> </Button>
            </Col>
            <Col xs={12}>
              <Button variant="outline-primary" className="w-100 p-3" onClick={() => setModalView("my-visuals")}> <FontAwesomeIcon icon={faImages} className="me-2" size="lg" /> <div>Select from Your Visuals & Assets</div> </Button>
            </Col>
          </Row>
        );
      case "upload":
        return (
          <div>
            <Button variant="link" className="mb-3" onClick={handleBackToOptions}> <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to options </Button>
            <FileUploadForm isLoggedIn={!!userInfo} onVisualAdded={handleAddVisual} isOrg={false} hideSubmitButton={true} isInPresentation={true} submitButtonText="Add to Builder" lightTheme={lightTheme} onSuccess={() => { setShowSuccessMessage(true); setTimeout(() => { setShowSuccessMessage(false); handleBackToOptions(); }, 1000); }} />
          </div>
        );
      case "search":
        return (
          <div>
            <Button variant="link" className="mb-3" onClick={handleBackToOptions}> <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to options </Button>
            <SearchComponent
              isOrg={owner === 'team'}
              onVisualAdded={handleAddVisual}
              lightTheme={true}
              onlyOneVisual={true}
              setIsUploading={setIsCreatingVisual}
              initialSearchQuery={initialSearchQuery}
            />
          </div>
        );
      case "web":
        return (
          <div>
            <Button variant="link" className="mb-3" onClick={handleBackToOptions}> <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to options </Button>
            <WebUploadForm isLoggedIn={!!userInfo} onVisualAdded={handleAddVisual} isOrg={false} hideSubmitButton={true} isInPresentation={true} submitButtonText="Add to Builder" lightTheme={lightTheme} onSuccess={() => { setShowSuccessMessage(true); setTimeout(() => { setShowSuccessMessage(false); handleBackToOptions(); }, 1000); }} />
          </div>
        );
      case "my-visuals":
        return (
          <div>
            <Button variant="link" className="mb-3" onClick={handleBackToOptions}> <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to options </Button>
            <div className="mb-4">
              <div className="d-flex justify-content-center align-items-center">
                <Form.Control
                  type="text"
                  placeholder="Search my library..."
                  onChange={(e) => updateVisFilterTerm(e.target.value)}
                  id="my-visuals-search-bar"
                  className="search-bar"
                  style={{ maxWidth: '400px' }}
                />
              </div>
              {visList.some(v => v.hide_in_library || v.hideInLibrary) && (
                <Tabs defaultActiveKey="visuals" activeKey={selectedVisualTab} onSelect={(key) => setSelectedVisualTab(key)} className="visual-picker-tabs">
                  <Tab eventKey="visuals" title="Visuals" className="visual-picker-tab" />
                  <Tab eventKey="assets" title="Other Assets" className="visual-picker-tab" />
                </Tabs>
              )}
            </div>
            <Row className="g-3">

              {visList.map((visual) => {
                const isPresentationType = visual.type === 'presentation' || visual.type === 'slideshow' || visual.type === 'scrollytelling';
                const isExplainerType = visual.topLevelType === 'explainer' || visual.type === 'explainer' || visual.type === 'tour';
                const isAlreadyAdded = filterVisualIdsList.includes(visual.id);
                
                return (
                  <Col xs={12} sm={6} md={4}
                    key={visual.id}
                    onClick={() => !isAlreadyAdded && handleAddVisual(visual)}
                    className={`picker-visual-card ${isAlreadyAdded ? 'already-added' : ''}`}
                    data-visual-id={visual.id}
                    data-type={visual.hide_in_library ? "assets" : "visuals"}
                    data-visual-title={visual.title?.toLowerCase() || ""}
                    data-visual-description={visual.description?.toLowerCase() || ""}
                    style={{ 
                      opacity: isAlreadyAdded ? 0.5 : 1,
                      cursor: isAlreadyAdded ? 'not-allowed' : 'pointer',
                      position: 'relative'
                    }}>
                    {isAlreadyAdded && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: '#10b981',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        zIndex: 10,
                        pointerEvents: 'none'
                      }}>
                        ✓ Added
                      </div>
                    )}
                    {isPresentationType ? (
                      <CarouselPresentationCard 
                        presentation={visual} 
                        onPresDelete={() => {}}
                        onPresEdit={() => {}}
                        blockActions={true} 
                      />
                    ) : isExplainerType ? (
                      <CarouselExplainerCard 
                        explainer={visual}
                        onExplainerDelete={() => {}}
                        onExplainerEdit={() => {}}
                        blockActions={true}
                      />
                    ) : (
                      <CarouselVisCard 
                        vis={visual} 
                        blockVisLink={true} 
                        blockActions={true} 
                      />
                    )}
                  </Col>
                );
              })}
            </Row> {initialized ?
              (!visList || visList.length === 0) ? (
                <div className="text-center mt-4">
                  <p>No visuals found in your collection.</p>
                </div>
              ) : (
                <div className="text-center mt-4" id="empty-search-results" style={{ display: "none" }}>
                  <p>No visuals in your collection match your search.</p>
                </div>
              )
              : (<div className="text-center mt-4">
                <RBSpinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </RBSpinner>
              </div>
              )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {modalView === "options" ? "Select Visual" :
            modalView === "upload" ? "Upload Images or Models" :
              modalView === "search" ? "Search Visuals" :
                modalView === "my-visuals" ? "Your Visuals" :
                  "Use Web Resource"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderModalContent()}</Modal.Body>
    </Modal>
  );
};

export default VisualPickerModal; 